/**
 * BuilderPreviewPage
 *
 * This page is loaded inside an iframe to render the builder preview.
 * It receives UI definitions via postMessage and renders them using
 * the production SDUI renderer.
 *
 * When an `archeId` URL param is present, uses the production ArcheProvider
 * so AI actions, file operations, and all server-side features work identically
 * to production. Falls back to BuilderPreviewProvider when no archeId is present.
 *
 * Communication protocol:
 * Parent -> Iframe:
 *   { type: 'UPDATE_UI', payload: { uiDefinition, theme, input } }
 *   { type: 'UPDATE_THEME', payload: { theme: 'light' | 'dark' } }
 *   { type: 'RESET' }
 *
 * Iframe -> Parent:
 *   { type: 'READY' }
 *   { type: 'ACTION_EXECUTED', payload: { actionId, actionType } }
 *   { type: 'ERROR', payload: { message } }
 *   { type: 'LOG_ENTRY', payload: LogEntry }
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { UIDefinition, SDUIComponent, PageDefinition } from '@fias/sdui-types';
import { BuilderPreviewProvider } from '../providers/BuilderPreviewProvider';
import { ArcheProvider } from '@/arche/providers/ArcheProvider';
import { useArcheContext } from '@/sdui/hooks/useArcheContext';
import { PageRenderer } from '@/sdui/renderer/PageRenderer';
import { getWebSocket, FiasWebSocket } from '@/lib/websocket';
import { authApi } from '@/lib/auth-api';
import type { LogLevel, LogCategory } from '../types/log-entry';
import '@/index.css'; // Include global styles

/**
 * Builder component format (simplified, from Claude tools)
 */
interface BuilderComponent {
  type: string;
  id?: string;
  props?: Record<string, unknown>;
  style?: Record<string, unknown>;
  children?: BuilderComponent[];
  visible?: string | boolean;
  events?: Record<string, unknown>;
  // The AI sometimes nests these inside the root component
  input?: Record<string, unknown>;
  aiConfigs?: Record<string, unknown>;
}

/**
 * Builder UI definition format (from Claude tools)
 */
interface BuilderUIDefinition {
  root?: BuilderComponent;
  actions?: Record<string, unknown>;
  input?: Record<string, unknown>;
  aiConfigs?: Record<string, unknown>;
  version?: string;
}

/**
 * Message types from parent
 */
type ParentMessage =
  | {
      type: 'UPDATE_UI';
      payload: {
        uiDefinition: BuilderUIDefinition | null;
        theme?: 'light' | 'dark';
        input?: Record<string, unknown>;
      };
    }
  | { type: 'UPDATE_THEME'; payload: { theme: 'light' | 'dark' } }
  | { type: 'SET_BLOCKLIST'; payload: { enabled: boolean } }
  | { type: 'CAPTURE_SCREENSHOT' }
  | { type: 'RESET' };

/**
 * Convert builder component type to production component type
 * Builder uses lowercase (container, button), production uses PascalCase (Container, Button)
 */
function convertComponentType(type: string): string {
  // Map lowercase builder types to PascalCase production types
  const typeMap: Record<string, string> = {
    container: 'Container',
    text: 'Text',
    button: 'Button',
    input: 'TextInput',
    textarea: 'TextArea',
    numberinput: 'NumberInput',
    select: 'Select',
    checkbox: 'Checkbox',
    radiogroup: 'RadioGroup',
    fileinput: 'FileInput',
    filepicker: 'FilePicker',
    image: 'Image',
    card: 'Card',
    form: 'FormGroup',
    markdown: 'Markdown',
    divider: 'Divider',
    spacer: 'Spacer',
    heading: 'Heading',
    label: 'Label',
    badge: 'Badge',
    alert: 'Alert',
    icon: 'Icon',
    link: 'Link',
    tabs: 'Tabs',
    grid: 'Grid',
    flex: 'Flex',
    stepper: 'Stepper',
    progressbar: 'ProgressBar',
    bulletlistinput: 'BulletListInput',
    reviewlist: 'ReviewList',
    table: 'Table',
    loading: 'Loading',
    skeleton: 'Skeleton',
    emptystate: 'EmptyState',
    formgroup: 'FormGroup',
    buttongroup: 'ButtonGroup',
    collapsible: 'Collapsible',
    accordion: 'Accordion',
    panel: 'Panel',
    splitpane: 'SplitPane',
  };

  return typeMap[type.toLowerCase()] || type;
}

/**
 * Generate a unique ID if not provided
 */
let idCounter = 0;
function generateId(type: string): string {
  return `${type}-${++idCounter}`;
}

/**
 * Check if a value is a valid component object
 */
function isValidComponent(value: unknown): value is BuilderComponent {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as BuilderComponent).type === 'string'
  );
}

/**
 * Convert builder component to production SDUIComponent format
 */
function convertComponent(builderComponent: BuilderComponent): SDUIComponent {
  const type = convertComponentType(builderComponent.type);
  const id = builderComponent.id || generateId(type);

  // Build props (without style - style goes at top level)
  const props: Record<string, unknown> = { ...builderComponent.props };

  // Convert children recursively, filtering out invalid entries
  const children = builderComponent.children
    ?.filter((child) => {
      if (!isValidComponent(child)) {
        console.warn(
          '[BuilderPreviewPage] Skipping invalid child (not a component object):',
          child,
        );
        return false;
      }
      return true;
    })
    .map(convertComponent);

  // Build the component object
  const componentBase: Record<string, unknown> = {
    type,
    id,
    props,
  };

  // Add style at top level (not inside props) so ComponentRenderer can pass it to components
  if (builderComponent.style && Object.keys(builderComponent.style).length > 0) {
    componentBase.style = builderComponent.style;
  }

  if (children && children.length > 0) {
    componentBase.children = children;
  }

  // Convert events
  if (builderComponent.events) {
    componentBase.events = builderComponent.events;
  }

  // Add visible at component level (not inside props) for conditional rendering
  if (builderComponent.visible !== undefined) {
    componentBase.visible = builderComponent.visible;
  }

  // Cast to SDUIComponent - the runtime type will be validated by the renderer
  return componentBase as unknown as SDUIComponent;
}

/**
 * Convert builder UI definition to production UIDefinition format
 */
export function convertToUIDefinition(builderDef: BuilderUIDefinition | null): UIDefinition | null {
  if (!builderDef || !builderDef.root) {
    return null;
  }

  // Reset ID counter for each conversion
  idCounter = 0;

  // Convert root component to layout array
  const rootComponent = convertComponent(builderDef.root);

  // Create a single page with the converted component
  const mainPage: PageDefinition = {
    id: 'main',
    title: 'Preview',
    layout: [rootComponent],
  };

  // Add actions to the page if present (check top-level and inside root)
  const actions =
    builderDef.actions || (builderDef.root as unknown as Record<string, unknown>)?.actions;
  if (actions && typeof actions === 'object') {
    mainPage.actions = actions as Record<string, import('@fias/sdui-types').ActionDefinition>;
  }

  // Build the full UI definition
  const uiDefinition: UIDefinition = {
    version: (builderDef.version || '1.0') as '1.0',
    defaultPage: 'main',
    pages: {
      main: mainPage,
    },
  };

  // Preserve aiConfigs at top level (builder format stores them here)
  const aiConfigs = builderDef.aiConfigs || builderDef.root?.aiConfigs;
  if (aiConfigs) {
    uiDefinition.aiConfigs = aiConfigs as UIDefinition['aiConfigs'];
  }

  // Carry over input as initialInput so ArcheProvider can initialize state
  // (e.g., currentStep for steppers, default field values)
  const input = builderDef.input || (builderDef.root?.input as Record<string, unknown> | undefined);
  if (input && typeof input === 'object') {
    uiDefinition.initialInput = input;
  }

  return uiDefinition;
}

/**
 * Categorize a console message based on known prefix patterns
 */
function categorizeMessage(message: string): { category: LogCategory; source?: string } {
  if (message.startsWith('[BuilderPreviewPage]')) {
    return { category: 'render', source: 'BuilderPreviewPage' };
  }
  if (message.startsWith('[ComponentRenderer]')) {
    return { category: 'event', source: 'ComponentRenderer' };
  }
  if (message.startsWith('[Button]')) {
    return { category: 'event', source: 'Button' };
  }
  if (message.startsWith('[BuilderPreview]') || message.startsWith('[Preview]')) {
    if (/expression|condition|compute/i.test(message)) {
      return { category: 'expression', source: 'BuilderPreviewProvider' };
    }
    if (/page|state|definition|updates/i.test(message)) {
      return { category: 'state', source: 'BuilderPreviewProvider' };
    }
    if (/form|submit/i.test(message)) {
      return { category: 'event', source: 'BuilderPreviewProvider' };
    }
    return { category: 'action', source: 'BuilderPreviewProvider' };
  }
  return { category: 'general' };
}

/**
 * Safely serialize a value for postMessage (structured clone safe)
 */
function safeSerialize(value: unknown): unknown {
  if (value === undefined || value === null) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  try {
    return JSON.parse(
      JSON.stringify(value, (_key, val) => {
        if (typeof val === 'function') return '[Function]';
        if (typeof val === 'symbol') return `[Symbol: ${val.toString()}]`;
        if (val instanceof HTMLElement) return `[HTMLElement: ${val.tagName}]`;
        if (val instanceof Error) return { message: val.message, stack: val.stack };
        return val;
      }),
    );
  } catch {
    return String(value);
  }
}

/**
 * Known framework noise patterns to suppress from log forwarding.
 * Messages matching any pattern are still logged to the real console
 * but are NOT forwarded via postMessage. Add new patterns here as
 * we discover more unhelpful framework output.
 */
const BLOCKED_LOG_PATTERNS = [
  /^Download the React DevTools/,
  /^\[HMR\]/,
  /^\[vite\]/,
  /^%c/, // styled console messages from frameworks
  /^Warning: /, // React dev warnings
  /^React does not recognize/,
  /^\[ComponentRenderer\] Component has events:/, // re-render noise (StrictMode doubles these)
];

/** Module-level flag toggled via SET_BLOCKLIST message from parent */
let blocklistEnabled = true;

function isBlockedMessage(message: string): boolean {
  if (!blocklistEnabled) return false;
  return BLOCKED_LOG_PATTERNS.some((pattern) => pattern.test(message));
}

let logIdCounter = 0;

/**
 * Intercept console methods inside the iframe and forward log entries
 * to the parent window via postMessage.
 */
function setupConsoleInterceptor(): () => void {
  const originals = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
  };

  const levels: LogLevel[] = ['log', 'warn', 'error', 'info'];

  for (const level of levels) {
    console[level] = (...args: unknown[]) => {
      originals[level].apply(console, args);

      const message =
        typeof args[0] === 'string'
          ? args[0]
          : args.map((a) => (typeof a === 'string' ? a : String(a))).join(' ');

      // Skip forwarding known framework noise
      if (isBlockedMessage(message)) {
        return;
      }

      const { category, source } = categorizeMessage(message);

      const data = args.length > 1 ? safeSerialize(args.slice(1)) : undefined;

      const entry = {
        id: `log-${++logIdCounter}`,
        timestamp: Date.now(),
        level,
        category,
        message,
        data,
        source,
      };

      try {
        window.parent.postMessage({ type: 'LOG_ENTRY', payload: entry }, '*');
      } catch {
        try {
          window.parent.postMessage(
            { type: 'LOG_ENTRY', payload: { ...entry, data: String(data) } },
            '*',
          );
        } catch {
          // Cannot send this entry
        }
      }
    };
  }

  return () => {
    for (const level of levels) {
      console[level] = originals[level];
    }
  };
}

/**
 * Inner content component for ArcheProvider mode.
 * Uses useArcheContext() to access the loaded UI and renders the current page.
 */
function ArchePreviewContent() {
  const { ui, currentPage } = useArcheContext();

  if (!ui || !currentPage) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <div className="text-4xl mb-2">Preview</div>
          <p className="text-sm">Your Arche will appear here as you build it</p>
        </div>
      </div>
    );
  }

  const page = ui.pages[currentPage];
  if (!page) return null;

  return <PageRenderer page={page} />;
}

export function BuilderPreviewPage() {
  // Read archeId from URL search params
  const archeId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('archeId');
  }, []);

  const [uiDefinition, setUiDefinition] = useState<UIDefinition | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [initialInput, setInitialInput] = useState<Record<string, unknown>>({});
  const initialInputSetRef = useRef(false);

  // Apply dark class to <html> so Tailwind dark: variants and CSS variables work
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);
  const [isReady, setIsReady] = useState(false);

  // Initialize and connect WebSocket for ArcheProvider mode (AI streaming needs it)
  useEffect(() => {
    if (!archeId) return;
    const wsEndpoint = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';
    if (!wsEndpoint) return;

    let ws: FiasWebSocket;
    try {
      ws = getWebSocket(wsEndpoint);
    } catch {
      ws = new FiasWebSocket({ endpoint: wsEndpoint });
    }

    if (!ws.isConnected()) {
      authApi.getValidIdToken().then((token) => {
        if (token) {
          ws.connect(token);
        }
      });
    }
  }, [archeId]);

  // Handle messages from parent
  const handleMessage = useCallback((event: MessageEvent) => {
    // Only accept messages from same origin (parent window)
    if (event.source !== window.parent) return;

    const message = event.data as ParentMessage;

    switch (message.type) {
      case 'UPDATE_UI': {
        const { uiDefinition: builderDef, theme: newTheme, input } = message.payload;
        const converted = convertToUIDefinition(builderDef);
        setUiDefinition(converted);
        if (newTheme) setTheme(newTheme);
        // Only set initial input on the first UI load or when explicitly passed.
        // Do NOT reset on incremental updates (edit_ui, add_action, etc.)
        // as that would wipe user-entered data.
        if (input) {
          // Explicitly passed input always takes effect (e.g., RESET)
          setInitialInput(input);
          initialInputSetRef.current = true;
        } else if (!initialInputSetRef.current) {
          // First load: extract input from builder definition
          const builderInput =
            (builderDef as BuilderUIDefinition)?.input ||
            ((builderDef as BuilderUIDefinition)?.root?.input as
              | Record<string, unknown>
              | undefined);
          if (builderInput && typeof builderInput === 'object') {
            setInitialInput(builderInput as Record<string, unknown>);
          }
          initialInputSetRef.current = true;
        }
        break;
      }

      case 'UPDATE_THEME': {
        setTheme(message.payload.theme);
        break;
      }

      case 'SET_BLOCKLIST': {
        blocklistEnabled = message.payload.enabled;
        break;
      }

      case 'CAPTURE_SCREENSHOT': {
        // Capture screenshot using html2canvas
        import('html2canvas-pro').then(({ default: html2canvas }) => {
          const root = document.getElementById('preview-root') || document.body;
          html2canvas(root, {
            useCORS: true,
            scale: 1,
            logging: false,
          })
            .then((canvas) => {
              const dataUrl = canvas.toDataURL('image/png', 0.7);
              window.parent.postMessage({ type: 'SCREENSHOT_RESULT', data: dataUrl }, '*');
            })
            .catch((err) => {
              console.error('[BuilderPreviewPage] Screenshot capture failed:', err);
            });
        });
        break;
      }

      case 'RESET': {
        setUiDefinition(null);
        setInitialInput({});
        initialInputSetRef.current = false;
        break;
      }
    }
  }, []);

  // Set up message listener and console interceptor
  useEffect(() => {
    const cleanupInterceptor = setupConsoleInterceptor();

    window.addEventListener('message', handleMessage);

    // Notify parent that we're ready
    window.parent.postMessage({ type: 'READY' }, '*');
    setIsReady(true);

    return () => {
      window.removeEventListener('message', handleMessage);
      cleanupInterceptor();
    };
  }, [handleMessage]);

  // Get current page from UI definition (for fallback mode)
  const currentPage = uiDefinition?.pages?.[uiDefinition.defaultPage || 'main'];

  return (
    <div
      id="preview-root"
      className={`bg-background text-foreground ${theme === 'dark' ? 'dark' : ''}`}
    >
      {!isReady && (
        <div className="flex items-center justify-center h-screen">
          <p className="text-muted-foreground">Loading preview...</p>
        </div>
      )}

      {isReady && archeId ? (
        // Production mode: use ArcheProvider for full server-side action support
        <ArcheProvider archeId={archeId} externalUiDefinition={uiDefinition} archeName="Preview">
          <ArchePreviewContent />
        </ArcheProvider>
      ) : (
        <>
          {isReady && !uiDefinition && (
            <div className="flex items-center justify-center h-screen">
              <div className="text-center text-muted-foreground">
                <div className="text-4xl mb-2">Preview</div>
                <p className="text-sm">Your Arche will appear here as you build it</p>
              </div>
            </div>
          )}

          {isReady && uiDefinition && currentPage && (
            <BuilderPreviewProvider
              uiDefinition={uiDefinition}
              initialInput={initialInput}
              theme={theme}
            >
              <PageRenderer page={currentPage} />
            </BuilderPreviewProvider>
          )}
        </>
      )}
    </div>
  );
}

export default BuilderPreviewPage;
